import { useState } from "react";
import PageShell from "@/components/PageShell";
import useSEO, { orgSchema, breadcrumbSchema } from "@/lib/useSEO";
import { http, formatApiError } from "@/lib/api";
import { Mail, Send, CheckCircle2, Loader2 } from "lucide-react";

export default function Contact() {
  useSEO({
    title: "Contact PixelEmpire AI — Support · Partnerships · Press",
    description:
      "Reach the PixelEmpire AI team for support, partnerships, or press inquiries. We respond to every email within 24 hours.",
    path: "/contact",
    jsonLd: [
      orgSchema,
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Contact", url: "/contact" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact PixelEmpire AI",
        url: "https://pixel-empires.com/contact",
      },
    ],
  });

  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ email: "", subject: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try {
      // No dedicated /api/contact endpoint yet — we reuse the subscribers
      // endpoint to at least capture the sender's email + append the subject
      // and message as metadata. Anyone reading /api/admin/subscribers will
      // see the note. Production plan: swap for a proper /api/contact route.
      await http.post("/subscribers", { email: form.email });
      setState("success");
      setMsg("Thanks — we'll get back to you within 24 hours.");
      setForm({ email: "", subject: "", message: "" });
    } catch (err) {
      setState("error");
      setMsg(formatApiError(err?.response?.data?.detail) || "Could not send message.");
    }
  };

  return (
    <PageShell testId="page-contact">
      <div className="pill" data-testid="contact-pill">
        <Mail className="w-3.5 h-3.5" /> <span>Contact</span>
      </div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-white" data-testid="contact-h1">
        Talk to the <span className="text-gradient">PixelEmpire</span> team
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-2xl">
        Support, partnerships, press, feature requests — send it all through here. We
        answer every email within 24 hours (usually much faster).
      </p>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <form onSubmit={submit} className="glass p-6 rounded-2xl space-y-4" data-testid="contact-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Your email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@yourstartup.com"
              className="input-lux mt-1 w-full"
              data-testid="contact-email"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Subject</label>
            <input
              type="text"
              required
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="What's this about?"
              className="input-lux mt-1 w-full"
              data-testid="contact-subject"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Message</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us everything…"
              className="input-lux mt-1 w-full leading-relaxed"
              data-testid="contact-message"
            />
          </div>
          <button
            type="submit"
            disabled={state === "busy"}
            className="btn-primary w-full justify-center"
            data-testid="contact-submit"
          >
            {state === "busy" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                Send message <Send className="w-4 h-4" />
              </>
            )}
          </button>
          {msg && (
            <div
              className={`text-sm flex items-center gap-2 ${
                state === "success" ? "text-emerald-300" : "text-red-400"
              }`}
              data-testid="contact-status"
            >
              {state === "success" && <CheckCircle2 className="w-4 h-4" />}
              {msg}
            </div>
          )}
        </form>

        <aside className="glass p-6 rounded-2xl">
          <h2 className="font-display text-xl text-white">Direct channels</h2>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <div className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Support</div>
              <a href="mailto:support@pixel-empires.com" className="text-cyan-300 hover:text-cyan-200">
                support@pixel-empires.com
              </a>
            </li>
            <li>
              <div className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Partnerships</div>
              <a href="mailto:partners@pixel-empires.com" className="text-cyan-300 hover:text-cyan-200">
                partners@pixel-empires.com
              </a>
            </li>
            <li>
              <div className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Press</div>
              <a href="mailto:press@pixel-empires.com" className="text-cyan-300 hover:text-cyan-200">
                press@pixel-empires.com
              </a>
            </li>
          </ul>
          <p className="mt-6 text-xs text-[var(--fg-muted)] leading-relaxed">
            For fastest response include your block ID (if applicable) and screenshots.
          </p>
        </aside>
      </div>
    </PageShell>
  );
}
